import { createContext, ReactNode, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';

// project-imports
import Loader from 'components/Loader';
import { authLogout, fetchMe } from 'api';
import { APP_DEFAULT_PATH, LOGIN_PATH } from 'config';
import { accountCanFor } from 'utils/accountPermissions';
import { readActiveAccount, writeActiveAccount } from 'utils/activeAccount';
import { isOAuthCallbackPath } from 'utils/return-to';

// types
import { AuthContextType, AuthProps } from 'types/auth';
import { AccountPermission, MeResponse, Permission } from 'types/console';

const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

// ==============================|| AUTH CONTEXT & PROVIDER ||============================== //
//
// Who the admin is and what they may see. There is no token in here: the session is an HttpOnly
// cookie, and `/v1/me` is the only source of truth. The permissions array drives presentation (which
// menu items and buttons appear); every API call is still checked on the server.
//
// A person may belong to several developer accounts. `activeAccount` is the one the console works as: the id
// is kept in localStorage (read by the axios layer for `X-Developer-Id`), and a stored id that is no longer
// among `me.accounts` falls back to the first account. Switching bumps `accountEpoch`, which remounts the
// developer pages so nothing keeps showing the previous account's data.

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [accountEpoch, setAccountEpoch] = useState<number>(0);
  const navigate = useNavigate();

  const completeLogin = useCallback((next: MeResponse) => {
    const accounts = next.accounts ?? [];
    const resolved = accounts.find((account) => account.id === readActiveAccount())?.id ?? accounts[0]?.id ?? null;

    // Written before the state is set: the first request the new page makes must already carry the right header.
    writeActiveAccount(resolved);
    setAccountId(resolved);
    setMe(next);
    dispatch({ type: LOGIN, payload: { isLoggedIn: true, user: next.user } });
  }, []);

  const dropSession = useCallback(() => {
    setMe(null);
    dispatch({ type: LOGOUT });
  }, []);

  // Start-up check. An expired access token is refreshed by the axios layer before this fails, so a
  // rejection here means there is no session at all.
  useEffect(() => {
    // On the OAuth callback pages the callback request creates the session. Probing `/v1/me` there races
    // it: the probe's refresh can expire the CSRF cookie the callback POST needs. The callback page hands
    // its payload to completeLogin() instead, so the start-up state is simply "not signed in" here.
    if (isOAuthCallbackPath(window.location.pathname)) {
      dropSession();

      return;
    }

    let active = true;

    fetchMe()
      .then((next) => {
        if (active) completeLogin(next);
      })
      .catch(() => {
        if (active) dropSession();
      });

    return () => {
      active = false;
    };
    // The start-up probe runs once, when the provider mounts; navigating later does not repeat it.
  }, [completeLogin, dropSession]);

  const refreshMe = useCallback(async () => {
    const next = await fetchMe();

    completeLogin(next);

    return next;
  }, [completeLogin]);

  const accounts = useMemo(() => me?.accounts ?? [], [me]);
  const activeAccount = useMemo(() => accounts.find((account) => account.id === accountId) ?? accounts[0] ?? null, [accounts, accountId]);

  const setActiveAccount = useCallback(
    (id: string) => {
      writeActiveAccount(id || null);
      setAccountId(id || null);
      setAccountEpoch((value) => value + 1);
      navigate(APP_DEFAULT_PATH);
    },
    [navigate]
  );

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } catch {
      // The session is going away locally either way; a failed revoke is not worth blocking on.
    }

    dropSession();

    // A full navigation discards every piece of client state, not only the auth state. The next
    // person to sign in on this browser must not inherit the previous admin's open pages.
    window.location.assign(LOGIN_PATH);
  }, [dropSession]);

  const can = useCallback((permission: Permission) => me?.permissions.includes(permission) ?? false, [me]);

  const accountCan = useCallback(
    (permission: AccountPermission, productId?: string | null) => accountCanFor(activeAccount, permission, productId),
    [activeAccount]
  );

  const value = useMemo<AuthContextType>(
    () => ({
      ...state,
      me,
      isPlatform: me?.platformRole != null,
      can,
      accounts,
      activeAccount,
      accountEpoch,
      setActiveAccount,
      accountCan,
      completeLogin,
      refreshMe,
      logout
    }),
    [state, me, can, accounts, activeAccount, accountEpoch, setActiveAccount, accountCan, completeLogin, refreshMe, logout]
  );

  // Nothing renders until the session is known, so no guard decides against a half-loaded state.
  if (!state.isInitialized) {
    return <Loader />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
